import { ChangeEvent, FC, useContext, useEffect, useState } from "react";
import { 
  Box, Grid, Paper, 
  Typography, TextField, 
  FormHelperText, Checkbox, FormControlLabel,
  TextFieldProps
} from "@mui/material";
import { NumericFormatField, RichContent, RichHTML } from "@gisce/oficina-virtual-components";
import { useForm, Controller, FormProvider, FieldErrors } from "react-hook-form"
import { zodResolver } from '@hookform/resolvers/zod';
import AccessTariffSelectField from "../customFields/AccessTariffSelectField";
import ElectricityProductSelect from "../customFields/ElectricityProductSelect";
import FileUploadField from "../../../form-fields/FileUploadField";
import RadioGroupField from "../../../form-fields/RadioGroupField";
import CouponField from "../../../form-fields/CouponField/CouponField";
import { useTranslation } from "react-i18next";
import useI18n from "@/hooks/useI18n";
import { IStepForm } from "../../types/stepsFormData";
import { useSettingsContext } from "../../../Settings/hooks/useSettingsContext";
import { SubmitButtonContext } from "../../Contractation";
import { useProducts } from "../../hooks/data/useProducts";
import CouponsHandler from "../customFields/CouponsHandler";
import { PeriodNums, PeriodTypes, PowerPeriods, StepElecContractFormData } from "./types/FormContract";
import { useFormContract } from "./hooks/useFormContract";
import { useAppSelector } from "@/hooks";
import { FetchCUPSAvailabilityData } from "../../types/cups";
import { allPowersFull } from "./utils/FormContract";

const FormContract: FC<IStepForm<StepElecContractFormData>> = ({
  type,
  onSubmit
}) => {

  const submitButtonContext = useContext(SubmitButtonContext);
  const Settings = useSettingsContext();
  const contractationSettings = Settings.contractation!.electricity;

  const { t } = useTranslation();
  const { currentLang } = useI18n();

  const [storedNew, setStoredNew] = useState<PowerPeriods>([0,0]);
  const [tariffHalt, setTariffHalt] = useState(false);
  
  const contractation = useAppSelector(state => state[`${type}Contractation`]);
  const contractStep = contractation.steps.contract as StepElecContractFormData;
  const installationCUPSSIPSData = contractation.steps.installation.cupsSIPSData as FetchCUPSAvailabilityData;
  
  const {
    defaultSIPSValues,
    SIPSPowerDataValid,
    validationSchema,
    getSIPSPowerData
  } = useFormContract(contractStep, installationCUPSSIPSData);

  const methods = useForm<StepElecContractFormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: contractation.latestStep < 3 ? 
      defaultSIPSValues : contractStep
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    resetField,
    getFieldState,
    formState: { errors, isSubmitting }
  } = methods;

  // RHF discriminated unions limitation 
  // https://stackoverflow.com/questions/74219465/typescript-react-hook-form-error-handling-with-zod-union-schema
  const selfconsErrors = errors.selfcons as FieldErrors<
    Extract<StepElecContractFormData["selfcons"], {enabled: true}>
  > | undefined;

  const powersErrors = errors.powers as FieldErrors<
    Extract<StepElecContractFormData["powers"], {change: true}>
  > | undefined;

  const watchedPowers = watch("powers");
  const watchedPromoCode = watch("promotionalCode");
  const watchedAccessTariff = watch("accessTariff");
  const watchedSelfcons = watch("selfcons");

  const onContractSubmit = async (values: StepElecContractFormData) => {
    await onSubmit(values)
  };

  useEffect(() => {
    submitButtonContext?.setValues({
      onClick: () => {handleSubmit(onContractSubmit)()},
      loading: isSubmitting,
      disabled: tariffHalt
    });
  }, [isSubmitting, handleSubmit, tariffHalt]);

  const productsData = useProducts({
    power: (watchedPowers.change ? 
      watchedPowers.new : watchedPowers.current
    ),
    selfcons: watchedSelfcons.enabled,
    accessTariff: watchedAccessTariff.id, 
    cups: contractation.steps.installation.cups, 
    promotionalCode: watchedPromoCode,
    lang: currentLang.value
  });

  const showCurrentPowers = (
    !SIPSPowerDataValid ||
    Settings.contractation?.electricity?.forceShowPowers
  )

  const showPowersChange: boolean = (
    Settings.contractation?.electricity?.showPowersChange && 
    (contractation.steps.installation.activeContract 
      || installationCUPSSIPSData.sipsDataAvailable)
  )

  const showNewPowersFields = showPowersChange && watchedPowers.change;

  const handlePowerChange = (change: boolean) => {
    if (change) {
      setValue("powers.new.periods", storedNew);
      if (storedNew.some(val => val > 15)) {
        setValue(`powers.new.installationType`, "industrial");
      } else {
        setValue(`powers.new.installationType`, "domestic");
      }
    } else {
      setStoredNew(getValues("powers.new.periods"));
      resetField("powers.new.periods");
    }
  }

  const handleInitialPowerFieldChange = (
    type: PeriodTypes,
    number: 0 | 1, 
    value: number
  ) => {
    const periods = [
      number == 0 ? value : getValues(`powers.${type}.periods.0`),
      number == 1 ? value : getValues(`powers.${type}.periods.1`)
    ]
    if (periods.some(val => val > 15)) {
      if (getValues(`powers.${type}.installationType`) !== "industrial") {
        setValue(`powers.${type}.installationType`, "industrial");
        setValue(`powers.${type}.periods`, [periods[0], periods[1], 0, 0, 0, 0]);
      }
    } else {
      if (getValues(`powers.${type}.installationType`) !== "domestic") {
        resetField(`powers.${type}.periods.2`);
        resetField(`powers.${type}.periods.3`);
        resetField(`powers.${type}.periods.4`);
        resetField(`powers.${type}.periods.5`);
        setValue(`powers.${type}.installationType`, "domestic");
        setValue(`powers.${type}.periods`, [periods[0], periods[1]]);
      }
    }
  }

  const handleSelfconsChange = (value: boolean) => {
    if (value) {
      if (typeof installationCUPSSIPSData.selfconsCode == "string" && 
        installationCUPSSIPSData.selfconsCode !== '00') {
        setValue("selfcons.code", installationCUPSSIPSData.selfconsCode);
      } else {
        setValue("selfcons.code", '41');
      }
      if (!getValues("selfcons.attachments")) {
        setValue("selfcons.attachments", []);
      }
    } else {
      setValue("selfcons.code", '00');
      setValue("selfcons.attachments", []);
    }
  }

  useEffect(() => {
    if(productsData.isSuccess) {
      if (productsData.data.length === 1) {
        setValue("product.id", 
          productsData.data[0].id, { shouldValidate: true });
        setValue("product.name", productsData.data[0].name);
      } else {
        const currentProduct = productsData.data.find(
          prod => prod.id === getValues("product.id"));
        if (currentProduct) {
          setValue("product.id", currentProduct.id);
          setValue("product.name", currentProduct.name);
        } else {
          setValue("product.id", -1);
          setValue("product.name", "");
        }
      }
    } else {
      setValue("product.id", -1);
      setValue("product.name", "");
    }
  }, [productsData.data, setValue, getValues]);

  useEffect(() => {
    if (
      allPowersFull(
        (watchedPowers.change ? watchedPowers.new : watchedPowers.current)
      ) &&
      Settings?.contractation?.electricity?.tariffHaltContractation?.length &&
      watchedAccessTariff.name && watchedAccessTariff.id >= 0
    ) {
      if (
        Settings.contractation?.electricity?.tariffHaltContractation.includes(
          watchedAccessTariff.name
        )
      ) {
        setTariffHalt(true);
      } else if (tariffHalt){
        setTariffHalt(false);
      }
    } else if (tariffHalt){
      setTariffHalt(false);
    }
  }, [
    Settings?.contractation?.electricity?.tariffHaltContractation,
    watchedAccessTariff.name
  ])

  const powerTextField = (props: TextFieldProps) => <TextField
    sx={{width: '100%'}}
    InputProps={{
      inputComponent: NumericFormatField,
      inputProps: {
        suffix:" kW",
        allowNegative: false,
        allowedDecimalSeparators: [","],
        decimalScale: 3,
        decimalSeparator: ",",
        thousandSeparator: ".",
      }
    }}
    variant="standard"
    {...props}
  />;

  const powerFieldController = (
    type: PeriodTypes,
    number: PeriodNums
  ) => {
    return <>
      <Controller
        name={`powers.${type}.periods.${number}`}
        control={control}
        render={({ field }) => powerTextField({
            ...field,
            label: t(`common:text.contractation_p${number+1}`),
            disabled: (
              Settings.contractation?.electricity?.showPowersChange && 
              (SIPSPowerDataValid && !watchedPowers.change)
            ),
            error: !!(powersErrors?.[type]?.periods?.[number]),
            onChange: (e) => {
              if (number == 0 || number == 1) {
                handleInitialPowerFieldChange(type, number, Number(e.target.value));
              }
              field.onChange(Number(e.target.value));
              getValues(`powers.${type}.periods`).forEach((_elm, idx) => {
                if (idx < 6) {
                  if (
                    getFieldState(
                      `powers.${type}.periods.${idx as PeriodNums}`
                    ).isDirty
                  ) {
                    trigger(`powers.${type}.periods.${idx as PeriodNums}`);
                  }
                }
              })
            },
            value: getFieldState(field.name).isDirty || field.value > 0 ? 
              field.value : undefined
          })
        }
      />
      {powersErrors?.[type]?.periods?.[number]?.message && 
        <FormHelperText error={true} id="area-field-error-text">
          {t(powersErrors?.[type].periods[number].message)}
        </FormHelperText>
      }
    </>
  };

  const powerFieldsCurrent = () => {
    const items = watchedPowers.current;
    const itemsArray: PeriodNums[] = items.installationType === "domestic" ? 
      [0, 1] : [0, 1, 2, 3, 4, 5];
    return <>
      <Grid container item sm={12} spacing={2}>
        {itemsArray.map((power) => (
          <Grid item xs={12} sm={6} key={power}>
            {SIPSPowerDataValid ? 
              powerTextField({
                label: t(`common:text.contractation_p${power+1}`),
                value: getSIPSPowerData(power),
                disabled: true,
              })
            :
              powerFieldController("current", power)
            }
          </Grid>
        ))}
      </Grid>
    </>
  };

  const powerFieldsNew = () => {
    if (showNewPowersFields) {
      const items = watchedPowers.change && watchedPowers.new;
      const itemsArray: PeriodNums[] = items.installationType === "domestic" ? 
        [0, 1] : [0, 1, 2, 3, 4, 5];
      return <>
        <Grid container item sm={12} spacing={2}>
          {itemsArray.map((power) => (
            <Grid item xs={12} sm={6} key={power}>
              {
                powerFieldController("new", power)
              }
            </Grid>
          ))}
        </Grid>
      </>
    }
    return undefined;
  };

  return (
    <FormProvider {...methods}>
      <form data-testid="form-step" onSubmit={handleSubmit(onContractSubmit)}>

        <Box sx={{ml: {md: 5}, mr: {md: 5}}}>

          {(contractationSettings?.selfcons?.showSellMessage ?? true) &&
            <Typography style={{fontWeight: "bold"}}>
              {t('common:text.contractation_selfcons_message')}
            </Typography>
          }
          
          <Grid container spacing={3}>

            <Grid container item sm={12} md={6} spacing={2}>

              {showCurrentPowers && (
                <Grid item sm={12}>
                  <Typography variant="h6">
                    {t('common:text.contractation_current_power')}
                  </Typography>
                  <Typography variant="body2">
                    <RichContent 
                      content={
                        t('common:text.contractation_current_power_desc')
                      }
                    />
                  </Typography>
                  {powerFieldsCurrent()}
                </Grid>
              )}

              {showPowersChange && (<>
                <Grid item sm={12}>
                  <Controller
                    name={"powers.change"}
                    control={control}
                    // TODO: Remove RadioGroupField
                    render={({ field }) => <RadioGroupField
                      label={
                        <>
                          <Typography variant="h6">
                            {t('common:text.contractation_change_power')}
                          </Typography>
                          <Typography variant="body2">
                            {t('common:text.contractation_change_power_desc')}
                          </Typography>
                        </>
                      }
                      row
                      radioElements={
                        [
                          {
                            label: t("common:text.generic_yes"), 
                            val: true, 
                            style: {width: "25%"}
                          },
                          {
                            label: t("common:text.generic_no"), 
                            val: false, 
                            style: {width: "25%"}
                          }
                        ]
                      }
                      meta={{
                        touched: !!powersErrors?.change?.message,
                        error: powersErrors?.change?.message,
                      }}
                      input={{
                        ...field,
                        onChange: (value: boolean) => {
                          handlePowerChange(value);
                          field.onChange(value);
                        }
                      }}
                    />}
                  />
                </Grid>
                {showNewPowersFields && <Grid item container spacing={3}>
                  {powerFieldsNew()}
                </Grid>}
              </>)}

              {allPowersFull(
                (watchedPowers.change ? 
                  watchedPowers.new : watchedPowers.current
                )
              ) && 
                <Grid item xs={12} sm={6}>
                  <AccessTariffSelectField
                    label={t('common:text.contractation_access_tariff')}
                    powers={(watchedPowers.change ? 
                      watchedPowers.new : watchedPowers.current
                    ).periods}
                    gas={false}
                  />
                </Grid>
              }
              
            </Grid>

            { (contractationSettings!.selfcons?.enabled ?? true) &&
              <Grid item sm={12} md={6}>
                <Paper style={{padding: 15}}>
                  { contractationSettings?.selfcons?.radioGroupVariant ?
                    <Controller
                      name={"selfcons"}
                      control={control}
                      render={({ field }) => <RadioGroupField
                        label={
                          <Typography variant="h6" color="primary">
                            {t('common:text.contractation_selfcons')}
                          </Typography>
                        }
                        radioElements={
                          [
                            {
                              label: t("common:text.generic_yes"), 
                              val: true, style: {width: "25%"}
                            },
                            {
                              label: t("common:text.generic_no"), 
                              val: false, style: {width: "25%"}
                            }
                          ]
                        }
                        meta={{
                          touched: !!errors.selfcons?.message,
                          error: errors.selfcons?.message,
                        }}
                        input={field}
                      />}
                    />
                  :
                    <>
                      <Controller
                        name={"selfcons.enabled"}
                        control={control}
                        render={({ field }) => <FormControlLabel
                          control={<Checkbox
                            {...field}
                            onChange={(
                              event: ChangeEvent<HTMLInputElement>, 
                              checked: boolean
                            ) => {
                              handleSelfconsChange(checked);
                              field.onChange(event);
                            }}
                            checked={field.value}
                            />} 
                          label={
                            <Typography variant="h6">
                              {t('common:text.contractation_selfcons')}
                            </Typography>
                          } 
                        />}
                      />
                      {errors.selfcons?.message &&
                        <FormHelperText error={true}>
                          {t(errors.selfcons.message)}
                        </FormHelperText>
                      }
                    </>
                  }
                  {watchedSelfcons.enabled && <>
                    <Controller
                      name={"selfcons.attachments"}
                      control={control}
                      render={({ field }) => <FileUploadField
                        min={1}
                        max={3}
                        label={
                          t('common:text.contractation_selfcons_document') +
                          (!!contractationSettings!.selfcons?.requireDocumentation ? "*" : "")
                        }
                        hint={t('common:text.contractation_selfcons_helper')}
                        anotherLabel={t('common:text.contractation_selfcons_add')}
                        removeLabel={t('common:text.remove')}
                        meta={{
                          touched: !!selfconsErrors?.attachments,
                          error: errors && Array.isArray(selfconsErrors?.attachments) && selfconsErrors.attachments.map(err => err?.message),
                        }}
                        input={field}
                      />}
                    />
                    {!!(selfconsErrors?.attachments?.message) &&
                      <FormHelperText error={true}>
                        {t(selfconsErrors.attachments.message)}
                      </FormHelperText>
                    }
                  </>}
                </Paper>
              </Grid>
            }
          </Grid>

          {tariffHalt ? 

            <Box pt={3}>
              <RichHTML 
                html={
                  t("common:text.contractation.electricity.tariff_halt_message",
                    {
                      companyName: Settings.organization.name,
                      phone: Settings.organization.phone_crm,
                      logoSrc: "/images/logo.png"
                    }
                  )
                }
              />
            </Box>
          :
            watchedAccessTariff.id >= 0 && 
              allPowersFull(
                (watchedPowers.change ? 
                  watchedPowers.new : watchedPowers.current
                )
              ) && 
              (<>
                {Settings?.features?.promotionalCode?.contractation?.enabled &&
                  <>
                    <Box m={1} style={{marginLeft: 0}} color="textSecondary">
                      <Typography variant="h6">
                        {t('common:text.contractation_promotional_code')}
                      </Typography>
                      <Typography variant="body2" style={{marginBottom: 15}}>
                        {t('common:text.contractation_promotional_detail')}
                      </Typography>
                    </Box>

                    <CouponField 
                      association={{
                        type: "lead",
                        id: contractation.leadId
                      }}
                      checkSearchParams={true}
                      multiple={Settings.features.promotionalCode.contractation.multiple}
                      outterIsSubmitting={isSubmitting} 
                      type={type}
                      submitButtonLabel={t("common:text.contractation_promotional_apply")}
                    />

                    <CouponsHandler />
                  </>
                }

                <Box m={1} style={{marginLeft: 0}} color="textSecondary">
                  <Typography variant="h6">
                    {t('common:text.contractation_new_pricelist')}
                  </Typography>
                  <Typography variant="body2" style={{marginBottom: 15}}>
                    {t('common:text.contractation_current_pressure_warning')}
                  </Typography>
                </Box>
                <Box sx={{ml: {md: 5}, mr: {md: 5}}}>
                  <Controller
                    name={"product.id"}
                    control={control}
                    render={({ field }) => <ElectricityProductSelect
                      availableProducts={productsData.isSuccess ? productsData.data : []}
                      loading={productsData.isLoading}
                      input={{
                        ...field,
                        onChange: (value) => {
                          field.onChange(value);
                          setValue("product.name", productsData.data?.find(
                            prod => (prod.id === value))?.name
                          )
                        }
                      }}
                    />}
                  />
                  {!!(errors.product?.id?.message) &&
                    <FormHelperText error={true}>
                      {t(errors.product.id.message)}
                    </FormHelperText>
                  }
                </Box>
              </>
            )
          }

        </Box>

      </form>
    </FormProvider>
  );
};

export default FormContract;
